import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createPartner,
  getPartner,
  listPartners,
  updatePartner,
} from "../../../../api/adminPartnersApi";
import AdminStatePanel from "../layout/AdminStatePanel";
import AdminTableContainer from "../layout/AdminTableContainer";
import RequirePermission from "../../../../auth/RequirePermission";
import StatusBadge from "../../intelligence/StatusBadge";
import { useAuth } from "../../../../auth/useAuth";
import { formatDateTime } from "../users/adminUserFormatters";

function ModalBackdrop({ children, onClose, title }) {
  return (
    <div className="admin-re-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="admin-re-modal panel-card"
        role="dialog"
        aria-labelledby="admin-partner-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="admin-re-modal-header">
          <div>
            <p className="eyebrow">Partners</p>
            <h2 id="admin-partner-modal-title">{title}</h2>
          </div>
          <button type="button" className="midpoint-share-button" onClick={onClose}>
            Close
          </button>
        </header>
        <div className="admin-re-modal-body">{children}</div>
      </div>
    </div>
  );
}

function AdminPartnersPageContent({ onClose }) {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("partners.manage");

  const [partners, setPartners] = useState([]);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [editPartner, setEditPartner] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    partnerCode: "",
    partnerName: "",
    partnerType: "broker",
    websiteUrl: "",
    contactEmail: "",
    contactPhone: "",
    logoUrl: "",
    notes: "",
    isActive: true,
  });
  const [modalError, setModalError] = useState("");
  const [modalSaving, setModalSaving] = useState(false);

  const loadPartners = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      const body = await listPartners({
        search: search.trim() || undefined,
        isActive: activeFilter === "" ? undefined : activeFilter,
      });
      setPartners(body.partners || []);
    } catch (error) {
      setPartners([]);
      setErrorMessage(error.message || "Unable to load partners.");
    } finally {
      setIsLoading(false);
    }
  }, [search, activeFilter]);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  const openEdit = async (partnerOrganizationId) => {
    try {
      setModalError("");
      const body = await getPartner(partnerOrganizationId);
      const partner = body.partner;
      setEditPartner(partner);
      setForm({
        partnerCode: partner.partnerCode,
        partnerName: partner.partnerName,
        partnerType: partner.partnerType || "broker",
        websiteUrl: partner.websiteUrl || "",
        contactEmail: partner.contactEmail || "",
        contactPhone: partner.contactPhone || "",
        logoUrl: partner.logoUrl || "",
        notes: partner.notes || "",
        isActive: partner.isActive,
      });
    } catch (error) {
      setErrorMessage(error.message || "Unable to load partner.");
    }
  };

  const resetCreateForm = () => {
    setForm({
      partnerCode: "",
      partnerName: "",
      partnerType: "broker",
      websiteUrl: "",
      contactEmail: "",
      contactPhone: "",
      logoUrl: "",
      notes: "",
      isActive: true,
    });
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!canManage) {
      return;
    }

    setModalSaving(true);
    setModalError("");

    try {
      await createPartner({
        partnerCode: form.partnerCode.trim().toUpperCase(),
        partnerName: form.partnerName.trim(),
        partnerType: form.partnerType,
        websiteUrl: form.websiteUrl || null,
        contactEmail: form.contactEmail || null,
        contactPhone: form.contactPhone || null,
        logoUrl: form.logoUrl || null,
        notes: form.notes || null,
        isActive: form.isActive,
      });
      setCreateOpen(false);
      resetCreateForm();
      setSuccessMessage("Partner organization created.");
      await loadPartners();
    } catch (error) {
      setModalError(error.message || "Unable to create partner.");
    } finally {
      setModalSaving(false);
    }
  };

  const handleUpdate = async (event) => {
    event.preventDefault();
    if (!editPartner || !canManage) {
      return;
    }

    setModalSaving(true);
    setModalError("");

    try {
      await updatePartner(editPartner.partnerOrganizationId, {
        partnerName: form.partnerName.trim(),
        partnerType: form.partnerType,
        websiteUrl: form.websiteUrl || null,
        contactEmail: form.contactEmail || null,
        contactPhone: form.contactPhone || null,
        logoUrl: form.logoUrl || null,
        notes: form.notes || null,
        isActive: form.isActive,
      });
      setEditPartner(null);
      setSuccessMessage("Partner organization updated.");
      await loadPartners();
    } catch (error) {
      setModalError(error.message || "Unable to update partner.");
    } finally {
      setModalSaving(false);
    }
  };

  const formFields = (
    <>
      {createOpen ? (
        <label className="admin-users-field">
          <span>Partner code</span>
          <input
            type="text"
            value={form.partnerCode}
            onChange={(event) =>
              setForm((f) => ({ ...f, partnerCode: event.target.value.toUpperCase() }))
            }
            required
            placeholder="LIV_REALTY"
          />
        </label>
      ) : (
        <p className="muted">
          <strong>Code:</strong> {form.partnerCode}
        </p>
      )}
      <label className="admin-users-field">
        <span>Partner name</span>
        <input
          type="text"
          value={form.partnerName}
          onChange={(event) => setForm((f) => ({ ...f, partnerName: event.target.value }))}
          required
        />
      </label>
      <label className="admin-users-field">
        <span>Type</span>
        <select
          value={form.partnerType}
          onChange={(event) => setForm((f) => ({ ...f, partnerType: event.target.value }))}
        >
          <option value="broker">Broker</option>
          <option value="data_provider">Data provider</option>
          <option value="agency">Agency</option>
          <option value="developer">Developer</option>
          <option value="internal">Internal</option>
          <option value="other">Other</option>
        </select>
      </label>
      <label className="admin-users-field">
        <span>Website URL</span>
        <input
          type="url"
          value={form.websiteUrl}
          onChange={(event) => setForm((f) => ({ ...f, websiteUrl: event.target.value }))}
        />
      </label>
      <label className="admin-users-field">
        <span>Contact email</span>
        <input
          type="email"
          value={form.contactEmail}
          onChange={(event) => setForm((f) => ({ ...f, contactEmail: event.target.value }))}
        />
      </label>
      <label className="admin-users-checkbox">
        <input
          type="checkbox"
          checked={form.isActive}
          onChange={(event) => setForm((f) => ({ ...f, isActive: event.target.checked }))}
        />
        Active
      </label>
    </>
  );

  const kpis = useMemo(() => {
    const total = partners.length;
    const active = partners.filter((p) => p.isActive).length;
    const inactive = total - active;
    const totalUsers = partners.reduce((sum, p) => sum + (p.userCount ?? 0), 0);
    const totalSources = partners.reduce((sum, p) => sum + (p.sourceCount ?? 0), 0);
    const totalListings = partners.reduce((sum, p) => sum + (p.listingCount ?? 0), 0);

    return { total, active, inactive, totalUsers, totalSources, totalListings };
  }, [partners]);

  return (
    <div className="admin-dashboard admin-partners-dash">
      <header className="admin-dashboard__header">
        <div>
          <h1 className="admin-dashboard__title">Partners</h1>
          <p className="admin-dashboard__subtitle">
            Manage partner organizations, broker relationships, and data providers
          </p>
        </div>
        <div className="admin-dashboard__header-actions">
          {canManage && (
            <button
              type="button"
              className="admin-dashboard__action-btn admin-dashboard__action-btn--primary"
              onClick={() => {
                resetCreateForm();
                setCreateOpen(true);
                setEditPartner(null);
              }}
            >
              Create Partner
            </button>
          )}
          <button
            type="button"
            className="admin-dashboard__action-btn"
            onClick={loadPartners}
            disabled={isLoading}
          >
            Refresh
          </button>
          {onClose && (
            <button
              type="button"
              className="admin-dashboard__action-btn"
              onClick={onClose}
            >
              ← Dashboard
            </button>
          )}
        </div>
      </header>

      {!isLoading && !errorMessage && (
        <section className="admin-dashboard__kpis">
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Total Partners</span>
            <span className="admin-dash__kpi-value">{kpis.total}</span>
          </div>
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Active</span>
            <span className="admin-dash__kpi-value admin-dash__kpi-value--green">{kpis.active}</span>
          </div>
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Inactive</span>
            <span className={`admin-dash__kpi-value${kpis.inactive > 0 ? " admin-dash__kpi-value--amber" : ""}`}>
              {kpis.inactive}
            </span>
          </div>
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Users</span>
            <span className="admin-dash__kpi-value">{kpis.totalUsers}</span>
          </div>
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Sources</span>
            <span className="admin-dash__kpi-value">{kpis.totalSources}</span>
          </div>
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Listings</span>
            <span className="admin-dash__kpi-value">{kpis.totalListings}</span>
          </div>
        </section>
      )}

      {successMessage && (
        <div className="admin-re-toast admin-re-toast--success" role="status">
          {successMessage}
          <button type="button" className="admin-re-toast__dismiss" onClick={() => setSuccessMessage("")}>
            ×
          </button>
        </div>
      )}

      <section className="admin-dash__card admin-dash__card--wide admin-partners-dash__table-card">
        <div className="admin-partners-dash__toolbar">
          <div className="admin-partners-dash__filters">
            <input
              type="search"
              className="admin-source-controls-dash__search"
              placeholder="Search partners…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <select
              className="admin-source-controls-dash__select"
              value={activeFilter}
              onChange={(event) => setActiveFilter(event.target.value)}
            >
              <option value="">All statuses</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <span className="admin-geocode-dash__item-count">
            {partners.length} partner{partners.length !== 1 ? "s" : ""}
          </span>
        </div>

        {isLoading ? <AdminStatePanel variant="loading" message="Loading partners…" /> : null}

        {!isLoading && errorMessage ? (
          <AdminStatePanel variant="error" message={errorMessage} onRetry={loadPartners} />
        ) : null}

        {!isLoading && !errorMessage && (
          <AdminTableContainer>
            <table className="admin-data-health-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Type</th>
                  <th>Active</th>
                  <th>Users</th>
                  <th>Sources</th>
                  <th>Listings</th>
                  <th>Updated</th>
                  {canManage ? <th>Actions</th> : null}
                </tr>
              </thead>
              <tbody>
                {partners.length ? (
                  partners.map((partner) => (
                    <tr key={partner.partnerOrganizationId}>
                      <td><strong>{partner.partnerName}</strong></td>
                      <td><code>{partner.partnerCode}</code></td>
                      <td>{partner.partnerType}</td>
                      <td>
                        <StatusBadge
                          label={partner.isActive ? "Yes" : "No"}
                          tone={partner.isActive ? "confirmed" : "disputed"}
                        />
                      </td>
                      <td>{partner.userCount ?? 0}</td>
                      <td>{partner.sourceCount ?? 0}</td>
                      <td>{partner.listingCount ?? 0}</td>
                      <td>{formatDateTime(partner.updatedAt)}</td>
                      {canManage ? (
                        <td>
                          <button
                            type="button"
                            className="admin-re-inline-link"
                            onClick={() => openEdit(partner.partnerOrganizationId)}
                          >
                            Edit
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={canManage ? 9 : 8} className="muted">
                      No partners match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </AdminTableContainer>
        )}
      </section>

      {createOpen ? (
        <ModalBackdrop title="Create partner" onClose={() => setCreateOpen(false)}>
          <form className="admin-users-form" onSubmit={handleCreate}>
            {modalError ? <p className="admin-data-health-error">{modalError}</p> : null}
            {formFields}
            <button type="submit" className="report-submit-cta" disabled={modalSaving}>
              {modalSaving ? "Saving…" : "Create"}
            </button>
          </form>
        </ModalBackdrop>
      ) : null}

      {editPartner ? (
        <ModalBackdrop title="Edit partner" onClose={() => setEditPartner(null)}>
          <form className="admin-users-form" onSubmit={handleUpdate}>
            {modalError ? <p className="admin-data-health-error">{modalError}</p> : null}
            {formFields}
            <p className="muted">
              Users: {editPartner.users?.length ?? 0} · Sources:{" "}
              {editPartner.sources?.length ?? 0}
            </p>
            <button type="submit" className="report-submit-cta" disabled={modalSaving}>
              {modalSaving ? "Saving…" : "Save"}
            </button>
          </form>
        </ModalBackdrop>
      ) : null}
    </div>
  );
}

const AdminPartnersPage = (props) => (
  <RequirePermission permission="partners.view">
    <AdminPartnersPageContent {...props} />
  </RequirePermission>
);

export default AdminPartnersPage;
